import { Outlet, Link, useLocation, useParams } from 'react-router-dom';

import { Breadcrumb } from '@/components/common/Breadcrumb';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { SkeletonBlock } from '@/components/common/SkeletonBlock';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useGetOrganizationQuery } from '@/features/orgs/api/orgApi';
import { useGetProjectQuery } from '@/features/projects/api/projectsApi';

import { useGetReportProjectSummaryQuery, useGetReportProjectsQuery } from './reportsApi';
import { plural } from './utils';

const KNOWN_ISSUES_TOOLTIP =
  "Active reports that haven't been resolved. Similar new reports are folded into these as duplicates.";

/**
 * Report-project detail layout (design §7): breadcrumb, H1, one muted counts
 * line from `GET …/:id/summary`, URL-synced Environments/Reports tabs, and the
 * tab's page through `<Outlet/>` — the org-detail pattern reused inside the
 * reports-owned route subtree (no shell changes).
 */
export function ReportProjectDetailPage() {
  const {
    orgId = '',
    projectId = '',
    reportProjectId = '',
  } = useParams<{
    orgId: string;
    projectId: string;
    reportProjectId: string;
  }>();
  const location = useLocation();

  const summaryQuery = useGetReportProjectSummaryQuery(reportProjectId);
  const projectsQuery = useGetReportProjectsQuery(projectId);
  const projectQuery = useGetProjectQuery(projectId);
  const { data: organization } = useGetOrganizationQuery(orgId);

  const reportProject = projectsQuery.data?.find((p) => p.id === reportProjectId);
  const name = reportProject?.name ?? '…';

  const base = `/organizations/${orgId}/projects/${projectId}/reports/${reportProjectId}`;
  const activeTab = location.pathname.endsWith('/reports') ? 'reports' : 'environments';

  return (
    <div>
      <Breadcrumb
        className="mb-3"
        items={[
          { label: 'Home', to: '/' },
          { label: 'Organizations', to: '/organizations' },
          { label: organization?.name ?? '…', to: `/organizations/${orgId}/projects` },
          { label: 'Projects', to: `/organizations/${orgId}/projects` },
          {
            label: projectQuery.data?.name ?? '…',
            to: `/organizations/${orgId}/projects/${projectId}`,
          },
          { label: name, to: null },
        ]}
      />

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight" title={name}>
            {name}
          </h1>
          {summaryQuery.isLoading ? (
            <SkeletonBlock className="mt-1 h-4 w-64" rounded="sm" />
          ) : summaryQuery.isError || !summaryQuery.data ? null : (
            <p className="mt-1 text-sm text-muted-foreground">
              {`${plural(summaryQuery.data.environments, 'environment')} · ${plural(
                summaryQuery.data.reports,
                'report',
              )} · `}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help underline decoration-muted-foreground/50 underline-offset-2">
                    {plural(summaryQuery.data.activeKnownIssues, 'known issue')}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">{KNOWN_ISSUES_TOOLTIP}</TooltipContent>
              </Tooltip>
            </p>
          )}
        </div>

        <Tabs value={activeTab} aria-label="Report project sections">
          <TabsList>
            <TabsTrigger value="environments" asChild>
              <Link to={`${base}/environments`}>Environments</Link>
            </TabsTrigger>
            <TabsTrigger value="reports" asChild>
              <Link to={`${base}/reports`}>Reports</Link>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </header>

      {/* 403 on the summary GET → PolicyDeniedView; 404 → P1 404 + "Go back";
          5xx → "Try again" — all server-authoritative, scoped to the tab area (§7). */}
      {summaryQuery.isError ? (
        <QueryErrorView
          error={summaryQuery.error}
          label="report project"
          onRetry={() => void summaryQuery.refetch()}
        />
      ) : (
        <Outlet />
      )}
    </div>
  );
}
